"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";

interface FaqSectionProps {
  section: VisualCmsSection;
}

function readString(c: Record<string, unknown>, key: string): string {
  const v = c[key];
  return typeof v === "string" ? v : "";
}

export function FaqSection({ section }: FaqSectionProps) {
  const content = (section.content as Record<string, unknown>) ?? {};
  const rawTitle = readString(content, "title");
  const titleHighlight = readString(content, "titleHighlight");
  const title = rawTitle || (titleHighlight ? "" : "Frequently asked questions");
  const subtitle = readString(content, "subtitle");
  const items = (section.faq_items ?? []).filter((it) => it.is_active !== false);
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);
  const shell = sectionShellStyle(section);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            {title}
            {titleHighlight && (
              <>
                {title && " "}
                <span className="bg-gradient-to-r from-green-500 to-green-700 bg-clip-text text-transparent">
                  {titleHighlight}
                </span>
              </>
            )}
          </h2>
          {subtitle && (
            <p className="mt-3 text-base sm:text-lg text-current/70">{subtitle}</p>
          )}
        </div>

        {items.length === 0 ? (
          <div className="max-w-2xl mx-auto rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-current/60">
            No FAQ items yet — add some from the admin editor.
          </div>
        ) : (
          <div className="max-w-3xl mx-auto divide-y divide-current/10 rounded-xl border border-current/10 bg-current/[0.02]">
            {items.map((it) => {
              const open = openId === it.id;
              return (
                <div key={it.id} className="px-5">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : it.id)}
                    className="w-full flex items-center justify-between text-left py-4 group"
                    aria-expanded={open}
                  >
                    <span className="text-base sm:text-lg font-semibold pr-4">
                      {it.question}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>
                  {open && (
                    <div className="pb-5 pr-8 text-sm sm:text-base text-current/80 whitespace-pre-wrap">
                      {it.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
